import { colors } from 'shared/assets/styles'
import React, { Component } from 'react'
import styled from 'styled-components'
import {
    ButtonFooter,
    CatalogStepperIngredients,
    CatalogStepperOptions,
    CatalogStepperSuggestions,
    DisplayableProductHeroImage,
    Stepper
} from 'app/ecommerce/common/components'
import Modal from 'react-modal'
import {
    purchasable,
    updateQuantityInitIngredient,
    updateQuantityInitOption
} from 'shared/utils/ecommerce/utils'
import type { Product as ProductType } from 'shared/store/ecommerce/catalog'
import type {
    ItemInfos,
    PromotionOrigin,
    PurchasableItem
} from 'shared/store/ecommerce/order'
import { Layout } from 'app/ecommerce/common'
import {
    breakpoints,
    media,
    ResponsiveContainer,
    style
} from 'app/assets/styles'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import { H2 } from 'app/common/typography'
import type { ClickAndCollectPromotion, MetaShortDTO } from 'shared/types'
import type { ECommerceMetadata, EventMetadata } from 'shared/store/metadata'
import { AllergensAlert } from 'app/ecommerce/common/components/modal-alert'
import { Check } from 'app/common/forms'
import { formatPrice, isDefined, orderPickUpType } from 'shared/utils'
import { AlertModal } from 'app/common/modals'
import { Button, CaptionHintText, Spacer } from 'app-v2/design-systems'
import { Close, Info } from 'app/assets/icons'
import { COLOR, SIZE, SPACING } from 'app-v2/design-tokens'
import { ProductSheet } from 'app/products'
import ProductSheetModal from '../product-sheet-modal/ProductSheetModal'
import { CatalogComposableStepper } from '../../components/catalog/catalog-composable-stepper'

const { tertiary, crunchyGreen, crunchyGreenDisabled, grey } = colors
const { classicModalMobileStyle, centeredModalStyle } = style
const { TABLESERVICE, ONSITE, TABLE_ORDER } = orderPickUpType
const { medium } = breakpoints

type Props = {
    intl: IntlShape,
    currentOrder?: PurchasableItem,
    collectMode: string,
    currentItemInfos?: ItemInfos,
    currentProduct?: ProductType,
    price: number,
    isFromCart: boolean,
    promotion?: ClickAndCollectPromotion,
    promotionOrigin?: PromotionOrigin,
    custoAvailable?: boolean,
    extrasAvailable?: boolean,
    isImpersonalOperation?: boolean,
    orderInit?: PurchasableItem,
    currentSuggestions: Array<PurchasableItem>,
    updateOrder: (order: ?PurchasableItem) => void,
    setCurrentItem: (product: ?PurchasableItem) => void,
    validateOrder: (isFromCart: boolean, orderInit: ?PurchasableItem) => void,
    setCurrentSuggestions: (suggestions: Array<PurchasableItem>) => void,
    fetchAvailableFeatures: () => void,
    showAllergensAlert: boolean,
    pageMetadata: (payload: MetaShortDTO) => void,
    sendMeta: EventMetadata => void,
    sendECommerceMeta: ECommerceMetadata => void,
    maxQuantity: number,
    cartProducts: Array<PurchasableItem>,
    navigate: (to: string, state?: any) => void
}
type State = {
    showAllergensAlert: boolean,
    noIce: boolean,
    isPickupLaterModalActive: boolean,
    displayProductSheet: boolean,
    width: number,
    disabled: boolean
}

class Product extends Component<Props, State> {
    state = {
        showAllergensAlert: this.props.showAllergensAlert,
        noIce: false,
        isPickupLaterModalActive: false,
        displayProductSheet: false,
        width: window.innerWidth,
        disabled: false
    }
    constructor(props: Props) {
        super(props)
        const {
            setCurrentItem,
            currentItemInfos,
            currentProduct,
            isFromCart,
            isImpersonalOperation,
            promotion,
            promotionOrigin,
            collectMode
        } = props

        if (!isFromCart && currentItemInfos) {
            let purchasableProduct = purchasable(
                currentProduct,
                undefined,
                promotion,
                promotionOrigin
            )
            if (isImpersonalOperation && purchasableProduct) {
                purchasableProduct = {
                    ...purchasableProduct,
                    isImpersonalOperation: true
                }
            }
            if (currentProduct && currentProduct.hasIce) {
                purchasableProduct = { ...purchasableProduct, noIce: false }
            }
            if (
                currentProduct &&
                currentProduct.hasPickUpLater &&
                [TABLESERVICE, ONSITE, TABLE_ORDER].includes(collectMode)
            ) {
                purchasableProduct = {
                    ...purchasableProduct,
                    pickUpLater: false
                }
            }
            setCurrentItem(purchasableProduct)
        }
    }

    async componentDidMount() {
        const {
            currentProduct,
            fetchAvailableFeatures,
            sendECommerceMeta,
            pageMetadata,
            isFromCart,
            orderInit
        } = this.props
        fetchAvailableFeatures()
        if (currentProduct) {
            sendECommerceMeta({
                type: 'ecomm_product_screen',
                data: {
                    list: currentProduct.categoryName,
                    name: currentProduct.name,
                    id: currentProduct.id,
                    price: currentProduct.price,
                    category: currentProduct.categoryName
                }
            })
            pageMetadata({ title: currentProduct.name })
        }
        if (isFromCart && orderInit && isDefined(orderInit.noIce)) {
            await this.setState({ noIce: orderInit.noIce })
        }
    }

    componentWillUnmount() {
        this.props.setCurrentSuggestions([])
    }

    onClickAllergensPopup = (shouldRedirect: boolean) => {
        const { navigate, intl } = this.props
        this.setState({ showAllergensAlert: false })
        if (shouldRedirect) {
            navigate(
                intl.formatMessage({ id: 'routes.clickandcollect.catalog' })
            )
        }
    }

    updateQuantityProduct = (isMore: boolean) => {
        const { currentOrder, setCurrentItem, maxQuantity } = this.props
        if (currentOrder) {
            const quantity = isMore
                ? !maxQuantity || currentOrder.quantity < maxQuantity
                    ? (currentOrder.quantity += 1)
                    : currentOrder.quantity
                : currentOrder.quantity > 0
                ? (currentOrder.quantity -= 1)
                : 0
            setCurrentItem({ ...currentOrder, quantity })
        }
    }

    onClick = (canPickUpLater?: boolean) => {
        const { orderInit, isFromCart, validateOrder } = this.props
        !!canPickUpLater
            ? this.setState({
                  isPickupLaterModalActive: true
              })
            : validateOrder(isFromCart, orderInit)
    }

    onPickUpLaterClick = async (value: boolean, actionUrl: string) => {
        await this.updatePickUpLaterOption(value)
        this.onClick()
        this.props.navigate(actionUrl)
    }

    updateIceOption = async () => {
        const { currentOrder, setCurrentItem } = this.props
        await this.setState({ noIce: !this.state.noIce })
        if (currentOrder) {
            setCurrentItem({ ...currentOrder, noIce: this.state.noIce })
        }
    }

    updatePickUpLaterOption = (value: boolean) => {
        const { currentOrder, setCurrentItem } = this.props
        if (currentOrder) {
            setCurrentItem({
                ...currentOrder,
                pickUpLater: value
            })
        }
    }

    handleInfoClick = () => {
        this.setState({
            displayProductSheet: true
        })
    }

    renderProductSheetModal = () => {
        const { intl } = this.props
        const { displayProductSheet, width } = this.state
        const { routeId } = this.props.currentProduct
        const PRODUCT = intl.formatMessage({ id: 'routes.product' })
        return (
            <Modal
                isOpen={displayProductSheet}
                style={
                    width < medium
                        ? classicModalMobileStyle
                        : centeredModalStyle
                }
                centered>
                <CloseButtonContainer
                    onClick={() =>
                        this.setState({
                            displayProductSheet: false
                        })
                    }>
                    <Close color={COLOR.PRIMARY} size={24} />
                </CloseButtonContainer>
                {routeId && (
                    <ProductSheet
                        productPath={`${PRODUCT}/${routeId}`}
                        fromEcommerce
                    />
                )}
            </Modal>
        )
    }

    setDisabledButton = (disabled: boolean) => {
        this.setState({ disabled: disabled })
    }

    render() {
        const {
            intl,
            currentOrder,
            updateOrder,
            setCurrentSuggestions,
            currentProduct,
            price,
            currentSuggestions,
            isFromCart,
            promotion,
            extrasAvailable = false,
            custoAvailable = false,
            sendMeta,
            maxQuantity,
            cartProducts,
            navigate,
            collectMode
        } = this.props
        const {
            showAllergensAlert,
            noIce,
            isPickupLaterModalActive,
            disabled
        } = this.state
        const {
            id: productId,
            name,
            image,
            ingredients,
            options,
            hasIce,
            hasPickUpLater,
            composable
        } = currentProduct || {}
        const { id, promotions, quantity } = currentOrder || {}
        const canPickUpLater =
            [TABLESERVICE, ONSITE, TABLE_ORDER].includes(collectMode) &&
            hasPickUpLater

        const cartItem = cartProducts.find(
            product => currentOrder && product.id === id
        )
        if (
            cartItem &&
            cartItem.maxQuantity === cartItem.quantity &&
            !isFromCart
        ) {
            navigate(
                intl.formatMessage({
                    id: 'routes.clickandcollect.catalog'
                })
            )
        }
        const actionUrl = intl.formatMessage({
            id: isFromCart
                ? 'routes.clickandcollect.cart.summary'
                : 'routes.clickandcollect.catalog'
        })
        const hasPromotion = currentOrder && promotions && promotions.length > 0
        const displayPrice = hasPromotion && price === 0 ? undefined : price
        const displayOptions = options && options.length > 0
        const displayIngredients =
            !!ingredients &&
            ((extrasAvailable &&
                ingredients.findIndex(
                    product => !!(product && product.price)
                ) >= 0) ||
                (custoAvailable &&
                    ingredients.findIndex(
                        product => !(product && product.price)
                    ) >= 0))

        return (
            <Layout background="plain" border backEnabled>
                {showAllergensAlert && (
                    <AllergensAlert
                        onClick={this.onClickAllergensPopup}
                        onClose={() =>
                            this.setState({ showAllergensAlert: false })
                        }
                        showAllergensAlert={showAllergensAlert}
                    />
                )}
                <Container>
                    {isPickupLaterModalActive && (
                        <AlertModal
                            isOpen={isPickupLaterModalActive}
                            handleClose={() =>
                                this.setState({
                                    isPickupLaterModalActive: false
                                })
                            }
                            alertTitleKey={'ecommerce.catalog.pick.up.later'}
                            alertContentKey={
                                'ecommerce.catalog.pick.up.modal.content'
                            }
                            okLabel={'yes'}
                            leftLabel={'no'}
                            onLeftClick={() =>
                                this.onPickUpLaterClick(false, actionUrl)
                            }
                            onOkClick={() =>
                                this.onPickUpLaterClick(true, actionUrl)
                            }
                        />
                    )}
                    {currentProduct && (
                        <StyledResponsiveContainer>
                            <ProductContainer>
                                <Title>{name}</Title>
                                <Spacer size={SIZE.XXS} />

                                <StyledButton
                                    variant="text"
                                    small
                                    onClick={() => this.handleInfoClick()}
                                    color={colors.text}>
                                    <>
                                        <Info color={colors.text} size={16} />
                                        <CaptionHintText>
                                            {intl.formatMessage({
                                                id:
                                                    'clickandcollect.catalog.product.infos'
                                            })}
                                        </CaptionHintText>
                                    </>
                                </StyledButton>

                                <DisplayableProductHeroImage imageUrl={image} />
                                {hasIce && (
                                    <IceContainer>
                                        <Check
                                            labelKey="clickandcollect.catalog.drink.with.ice"
                                            checked={!noIce}
                                            color={grey}
                                            onClick={this.updateIceOption}
                                        />
                                    </IceContainer>
                                )}
                                {displayIngredients && !composable && (
                                    <CatalogStepperIngredients
                                        order={currentOrder}
                                        listIngredients={
                                            updateQuantityInitIngredient(
                                                ingredients,
                                                currentOrder
                                            ) || []
                                        }
                                        dispatchOrder={order =>
                                            updateOrder(order)
                                        }
                                        featureCustoAvailable={custoAvailable}
                                        featureExtraAvailable={
                                            !hasPromotion && extrasAvailable
                                        }
                                        sendMeta={sendMeta}
                                    />
                                )}
                                {composable && (
                                    <CatalogComposableStepper
                                        order={currentOrder}
                                        ingredients={
                                            updateQuantityInitIngredient(
                                                ingredients,
                                                currentOrder
                                            ) || []
                                        }
                                        onUpdateOrder={order =>
                                            updateOrder(order)
                                        }
                                        setDisabledButton={
                                            this.setDisabledButton
                                        }
                                        sendMeta={sendMeta}
                                        isFromCart={isFromCart}
                                    />
                                )}
                                {displayOptions && (
                                    <CatalogStepperOptions
                                        order={currentOrder}
                                        isFromCart={isFromCart}
                                        listOptions={
                                            updateQuantityInitOption(
                                                options,
                                                currentOrder
                                            ) || []
                                        }
                                        dispatchOrder={order =>
                                            updateOrder(order)
                                        }
                                    />
                                )}
                                {!displayIngredients &&
                                    !displayOptions &&
                                    !hasPromotion && (
                                        <StepperQuantityContainer>
                                            <Stepper
                                                value={quantity || 1}
                                                onPressLeft={() =>
                                                    this.updateQuantityProduct(
                                                        false
                                                    )
                                                }
                                                onPressRight={() =>
                                                    this.updateQuantityProduct(
                                                        true
                                                    )
                                                }
                                                colorLeft={
                                                    currentOrder &&
                                                    quantity === 0
                                                        ? crunchyGreenDisabled
                                                        : crunchyGreen
                                                }
                                                colorRight={
                                                    currentOrder &&
                                                    maxQuantity &&
                                                    quantity >= maxQuantity
                                                        ? crunchyGreenDisabled
                                                        : crunchyGreen
                                                }
                                                isBig
                                            />
                                        </StepperQuantityContainer>
                                    )}
                                {!isFromCart && !promotion && (
                                    <CatalogStepperSuggestions
                                        currentItem={currentProduct}
                                        currentSuggestions={currentSuggestions}
                                        setCurrentSuggestions={
                                            setCurrentSuggestions
                                        }
                                        cartProducts={cartProducts}
                                    />
                                )}
                            </ProductContainer>
                            <ButtonFooter
                                labelKey={
                                    isFromCart
                                        ? 'clickandcollect.product.footer.personalize'
                                        : displayPrice
                                        ? 'clickandcollect.product.footer.price'
                                        : 'clickandcollect.product.footer'
                                }
                                labelParams={{ price: formatPrice(price) }}
                                actionUrl={!canPickUpLater ? actionUrl : ''}
                                onClick={() => this.onClick(canPickUpLater)}
                                disabled={!quantity || disabled}
                                shouldStick
                            />
                        </StyledResponsiveContainer>
                    )}
                </Container>
                {true && (
                    <ProductSheetModal
                        handleClose={() =>
                            this.setState({
                                displayProductSheet: false
                            })
                        }
                        routeId={this.props.currentProduct.routeId}
                        width={this.state.width}
                        type={this.props.currentProduct.type}
                    />
                )}
            </Layout>
        )
    }
}

const StyledButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
`

const Container = styled.div`
    background-color: ${tertiary};
    min-height: calc(100vh - 55px);
    margin-top: 55px;
    ${media.medium`
        min-height: calc(100vh - 90px);
        margin-top: 90px;
    `};
`

const StyledResponsiveContainer = styled(ResponsiveContainer)`
    padding-bottom: 100px;
    ${media.medium`
        width: 70%;
    `};
`

const ProductContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 35px;
`

const StepperQuantityContainer = styled.div`
    margin-top: 50px;
    align-items: center;
`

const Title = styled(H2)`
    margin-top: ${SPACING.M};
    ${media.medium`
        margin-top: ${SPACING.XL};
    `};
`

const IceContainer = styled.div`
    margin-top: 50px;
`

const CloseButtonContainer = styled.div`
    margin: ${SPACING.M};
    cursor: pointer;
`

export const ProductComponent = injectIntl(Product)
